import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { default as Select } from 'antd/es/select';
import { useEffect, useMemo, useState } from 'react';
import { CountryFlag } from 'shared/src/CountryFlag';
import { getCountryCode } from 'shared/src/getCountryCode';

import type { FundingConfiguration } from 'api';

import { useOrganisationId } from 'hooks/context';

import { CopyToClipboardButton } from '@molecules/CopyToClipboardButton';

import { FundingChangesNotice } from './FundingChangesNotice';
import { FundingInstructionsWarning } from './FundingInstructionsWarning';
import type { AccountFundingProps } from './types';
import { abbreviateAddress } from './utils';

const { Option } = Select;

export const FundingSection = ({
  accountNumber,
  fundingInstructions,
  refetch
}: AccountFundingProps) => {
  const organisationId = useOrganisationId<number>();
  const [currentInstruction, setInstruction] = useState<string>(
    fundingInstructions[0].uuid
  );

  const instructionMap = useMemo(
    () =>
      Object.fromEntries(
        fundingInstructions.filter((i) => i.uuid).map((i) => [i.uuid, i])
      ),
    [fundingInstructions]
  );

  const [isCurrentInstructionUpdated, setIsCurrentInstructionUpdated] =
    useState<boolean>(instructionMap[currentInstruction]?.changed);

  const InstructionField = (
    label: string,
    value: string,
    displayValue?: string
  ): JSX.Element | null => {
    if (!value) return null;
    return (
      <div className="flex min-w-0 flex-col">
        <div className="text-sm text-content-secondary">{label}</div>

        <div className="min-w-0 break-words">
          <CopyToClipboardButton displayText={displayValue} text={value} />
        </div>
      </div>
    );
  };

  const CurrencyLabel = (label: string, changed: boolean): JSX.Element => {
    return (
      <div className="flex justify-between gap-2">
        <div className="text-sm">{label}</div>
        {changed && (
          <ExclamationCircleIcon className="size-5 stroke-2 text-feedback-attention" />
        )}
      </div>
    );
  };

  useEffect(() => {
    setIsCurrentInstructionUpdated(instructionMap[currentInstruction]?.changed);
    setInstruction(instructionMap[currentInstruction]?.uuid);
  }, [currentInstruction, instructionMap]);

  return fundingInstructions ? (
    <div className="flex flex-col gap-3 pt-2 first-of-type:pt-0">
      <div className="text-xs font-semibold text-content-secondary">
        Currency
      </div>

      <Select
        value={currentInstruction}
        className="w-full"
        onChange={setInstruction}
      >
        {fundingInstructions.map((instruction: FundingConfiguration) => (
          <Option key={instruction.uuid} value={instruction.uuid}>
            <div className="flex flex-col text-sm">
              <div className="flex flex-row items-center gap-2">
                <CountryFlag
                  className="!size-[22px]"
                  countryCode={getCountryCode(instruction.currency_iso)}
                  svg
                />
                <div className="flex flex-col overflow-hidden">
                  {CurrencyLabel(
                    instruction.currency_iso,
                    Boolean(instruction.changed)
                  )}
                </div>
              </div>
            </div>
          </Option>
        ))}
      </Select>

      {currentInstruction && (
        <div className="flex flex-col gap-2">
          {isCurrentInstructionUpdated ? (
            <FundingChangesNotice
              organisationId={organisationId}
              accountNumber={Number(accountNumber)}
              configurationUuid={currentInstruction}
              onAccepted={refetch}
            />
          ) : null}

          <div className="pt-2 font-semibold">Funding instructions</div>

          <div className="text-sm">
            Go to your online bank and make a funds transfer using the payment
            details below:
          </div>
        </div>
      )}
      {InstructionField(
        'Beneficiary Name',
        instructionMap[currentInstruction]?.beneficiary?.full_name || ''
      )}
      {InstructionField(
        'Beneficiary IBAN',
        instructionMap[currentInstruction]?.beneficiary?.iban || ''
      )}
      {InstructionField(
        'Beneficiary Bank',
        instructionMap[currentInstruction]?.beneficiary?.bank_name || ''
      )}
      {InstructionField(
        'Beneficiary Address',
        instructionMap[currentInstruction]?.beneficiary?.bank_address || ''
      )}
      {InstructionField(
        'Account Number',
        instructionMap[currentInstruction]?.beneficiary?.account_number || ''
      )}
      {InstructionField(
        'Beneficiary Sort Code',
        instructionMap[currentInstruction]?.beneficiary?.sort_code || ''
      )}
      {InstructionField(
        'Beneficiary Swift Code',
        instructionMap[currentInstruction]?.beneficiary?.swift_code || ''
      )}
      {InstructionField(
        'Beneficiary Protocol',
        instructionMap[currentInstruction]?.beneficiary?.crypto_network || ''
      )}
      {instructionMap[currentInstruction]?.beneficiary?.wallet_address &&
        InstructionField(
          'Beneficiary Wallet',
          instructionMap[currentInstruction]?.beneficiary?.wallet_address || '',
          abbreviateAddress(
            instructionMap[currentInstruction]?.beneficiary?.wallet_address ||
              ''
          )
        )}
      {InstructionField(
        'Reference',
        instructionMap[currentInstruction]?.funding_reference || ''
      )}

      <FundingInstructionsWarning
        instruction={instructionMap[currentInstruction]}
      />
    </div>
  ) : (
    <div className="pb-6">
      Funding information not available for this account.
    </div>
  );
};
